import { getGLBData } from '@proxies/getGLBData'
import { useAppStore } from '@store'
import { GLBData } from '@types'

// Sistema de caché global para GLB (backGLBInfo / payload de unidad)
const glbCache = new Map<string, any>()

/**
 * Limpia el caché de GLB. Útil cuando cambias de API para evitar usar datos de la API anterior.
 */
export function clearGLBCache() {
  glbCache.clear()
}

// Escuchar cambios en la URL de la API para limpiar el caché
if (typeof window !== 'undefined') {
  window.addEventListener('apiUrlChanged', () => {
    clearGLBCache()
  })
}

function readSceneGlbEntry(
  sceneMap: Map<string | number, any> | Record<string, any> | undefined,
  catalogID: string,
): { glb?: any; backGLBInfo?: any; glbData?: any } | undefined {
  if (!sceneMap) return undefined
  if (typeof (sceneMap as Map<string | number, any>).get === 'function') {
    return (sceneMap as Map<string | number, any>).get(catalogID)
  }
  return (sceneMap as Record<string, any>)[catalogID]
}

function sceneMapHas(
  sceneMap: Map<string | number, any> | Record<string, any> | undefined,
  catalogID: string,
): boolean {
  if (!sceneMap) return false
  if (typeof (sceneMap as Map<string | number, any>).has === 'function') {
    return (sceneMap as Map<string | number, any>).has(catalogID)
  }
  return catalogID in (sceneMap as Record<string, any>)
}

/**
 * Devuelve el payload de unidad (backGLBInfo) para un catalogID.
 * sceneGLB guarda `{ glb, backGLBInfo }` — NUNCA `.glbData`.
 */
export async function getGLB(
  glbInScene: Map<string | number, any> | Record<string, any>,
  catalogID: string,
  realCatalogID?: string,
) {
  if (glbCache.has(catalogID)) {
    return glbCache.get(catalogID)
  }

  let glbData: any = null

  if (sceneMapHas(glbInScene, catalogID)) {
    const sceneGLBData =
      readSceneGlbEntry(glbInScene as Map<string | number, any>, catalogID) ??
      useAppStore.getState().sceneGLB.get(catalogID)
    // Forma real del store: { glb, backGLBInfo }. `glbData` era un key incorrecto.
    glbData = sceneGLBData?.backGLBInfo ?? sceneGLBData?.glbData ?? null
    if (!glbData) {
      console.warn(
        '[getGLB] sceneGLB tiene entrada pero sin backGLBInfo; refetch API:',
        catalogID,
      )
      glbData = await getGLBData(catalogID, realCatalogID)
    }
  } else {
    glbData = await getGLBData(catalogID, realCatalogID)
  }

  if (glbData) {
    glbCache.set(catalogID, glbData)
  }

  return glbData as GLBData
}
